import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import CommonCard from '../../components/card/CommonCard';
import Toggle from '../../components/toggle';
import ReportCard from './components/reportCard';
import { EXERCISE_TARGETS, API_PAGE_SIZE } from '../../constants';
import { formatDateTime, formatDateTimeDisplay, getDateRange } from '../../utils/base';
import { physicalRecordAPI } from '../../actions/imsServer';

export const ReportStepUnion = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    // 사용자 정보
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // 뷰 상태 관리
    const [activeView, setActiveView] = useState(0); // 0: 주간, 1: 월간, 2: 연간

    // 데이터 상태 관리
    const [physicalRecords, setPhysicalRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 주간 데이터 상태 관리
    const [currentWeekIndex, setCurrentWeekIndex] = useState(0); // 현재 주간 인덱스

    // 월간 데이터 상태 관리
    const [currentMonthIndex, setCurrentMonthIndex] = useState(0); // 현재 월간 인덱스

    // 연간 데이터 상태 관리
    const [currentYearIndex, setCurrentYearIndex] = useState(0); // 현재 연간 인덱스

    // 선택된 바 상태 관리
    const [selectedBarIndex, setSelectedBarIndex] = useState(-1);

    // 선택된 바를 해제하는 함수
    const clearSelectedBar = () => {
        setSelectedBarIndex(-1);
    };

    // 데이터 로딩 함수
    const fetchPhysicalRecords = async () => {
        if (!meInfo.id) return;
        try {
            setLoading(true);
            setError(null);

            // 과거 3개월부터 현재까지의 데이터 로드
            const today = new Date();
            const threeMonthsAgo = new Date(today);
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

            const dateRange = getDateRange('yearly', threeMonthsAgo);
            let allRecords = [];
            let offset = 0;
            let page;
            do {
                const response = await physicalRecordAPI.getPhysicalRecords({
                    userId: meInfo.id,
                    state: 0,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    offset
                });
                page = response?.data?.physicalRecords || [];
                allRecords = allRecords.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            setPhysicalRecords(allRecords);
        } catch (err) {
            console.error('신체 기록 데이터 로딩 실패:', err);
            setError(err.message);
            setPhysicalRecords([]);
        } finally {
            setLoading(false);
        }
    };

    // 초기 데이터 로드
    useEffect(() => {
        fetchPhysicalRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPhysicalRecords는 의존성에서 제외
    }, [meInfo.id]);

    // 현재 주간 데이터 가져오기
    const getCurrentWeekData = () => {
        // 데이터 유무와 상관없이 항상 해당 주간의 7일 데이터 생성
        const today = new Date();
        const startOfWeek = new Date(today);
        // 월요일부터 시작하도록 조정 (월요일 = 1, 일요일 = 0)
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // 일요일이면 -6, 아니면 월요일까지의 오프셋
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));

        const weekData = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);

            // 실제 데이터에서 해당 날짜 찾기
            const dateStr = formatDateTime(date, '-', ':', 0, 10);
            const actualRecord = physicalRecords.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            weekData.push({
                day: dateStr,
                value: actualRecord?.totalSteps || 0,
                floors: actualRecord?.floors || 0,
                color: (actualRecord?.totalSteps || 0) >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.steps : theme.palette.figma.white[40]
            });
        }
        return weekData;
    };

    // 현재 월간 데이터 가져오기
    const getCurrentMonthData = () => {
        // 데이터 유무와 상관없이 항상 해당 월의 모든 날짜 데이터 생성
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        const year = targetMonth.getFullYear();
        const month = targetMonth.getMonth() + 1;
        const daysInMonth = new Date(year, month, 0).getDate();

        const monthData = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            // 실제 데이터에서 해당 날짜 찾기
            const actualRecord = physicalRecords.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            monthData.push({
                day: dateStr,
                value: actualRecord?.totalSteps || 0,
                floors: actualRecord?.floors || 0,
                color: (actualRecord?.totalSteps || 0) >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.steps : theme.palette.figma.white[40]
            });
        }
        return monthData;
    };

    // 현재 연간 데이터 가져오기
    const getCurrentYearData = () => {
        // 데이터 유무와 상관없이 항상 해당 연도의 12개월 데이터 생성
        const today = new Date();
        const targetYear = new Date(today);
        targetYear.setFullYear(today.getFullYear() + currentYearIndex);

        const year = targetYear.getFullYear();

        const yearData = [];
        for (let month = 1; month <= 12; month++) {
            const monthStr = `${year}-${String(month).padStart(2, '0')}`;

            // 해당 월의 모든 데이터를 찾아서 평균 계산
            const monthRecords = physicalRecords.filter(record => {
                const recordDate = new Date(record.date);
                return recordDate.getFullYear() === year && recordDate.getMonth() + 1 === month;
            });

            // 해당 월의 총 일수 계산
            const daysInMonth = new Date(year, month, 0).getDate();

            // 현재 월인 경우 오늘까지만 포함
            const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;
            const totalDaysInMonth = isCurrentMonth ? today.getDate() : daysInMonth;

            const monthlyStepsTotal = monthRecords.length > 0
                ? monthRecords.reduce((sum, record) => sum + (record.totalSteps || 0), 0)
                : 0;

            const monthlyStepsAverage = monthRecords.length > 0
                ? Math.round(monthlyStepsTotal / totalDaysInMonth)
                : 0;

            const monthlyGoalDays = monthRecords.filter(record => record.totalSteps >= EXERCISE_TARGETS.STEPS).length;

            // const monthlyFloors = monthRecords.length > 0
            //     ? Math.round(monthRecords.reduce((sum, record) => sum + (record.floors || 0), 0) / totalDaysInMonth)
            //     : 0;

            yearData.push({
                day: monthStr,
                value: monthlyStepsAverage,
                stepsTotal: monthlyStepsTotal,
                goalDays: monthlyGoalDays,
                //floors: monthlyFloors,
                color: monthlyStepsAverage >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.steps : theme.palette.figma.white[40]
            });
        }
        return yearData;
    };

    // Y축 값 계산
    const calculateYAxisValues = () => {
        let currentData;
        switch (activeView) {
            case 1: // 월간
                currentData = getCurrentMonthData();
                break;
            case 2: // 연간
                currentData = getCurrentYearData();
                break;
            default: // 0 : 주간
                currentData = getCurrentWeekData();
        }

        if (currentData.length === 0) {
            return {
                maxValue: 15000,
                minValue: 0,
                averageValue: 0,
                yAxisValues: [0, 6000, 12000]
            };
        }

        const values = currentData.map(item => item.value);
        const maxValue = Math.max(...values);
        // const minValue = Math.min(...values); // Currently unused
        const averageValue = Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);

        // Y축 값을 동적으로 계산 (최대값의 1.2배까지, 1000단위로 반올림)
        const maxYValue = Math.ceil(maxValue * 1.2 / 1000) * 1000;
        const midYValue = Math.round(maxYValue / 2 / 1000) * 1000;

        return {
            maxValue: maxYValue,
            averageValue: averageValue,
            yAxisValues: [0, midYValue, maxYValue]
        };
    };

    // 현재 주간 데이터를 기반으로 통계 계산
    const calculateCurrentWeekStats = () => {
        const currentWeekData = getCurrentWeekData();
        if (currentWeekData.length === 0) {
            return {
                value: 0,
                distance: 0,
                calories: 0,
                floors: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0
            };
        }

        // 해당 주간의 실제 데이터 가져오기
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));

        let totalSteps = 0;
        let totalDistance = 0;
        let totalCalories = 0;
        let totalFloors = 0;
        let goalDays = 0;
        let _actualDays = 0;

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            const dateStr = formatDateTime(date, '-', ':', 0, 10);

            const actualRecord = physicalRecords.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            if (actualRecord) {
                totalSteps += actualRecord.totalSteps || 0;
                totalDistance += actualRecord.distance || 0;
                totalCalories += actualRecord.calories || 0;
                totalFloors += actualRecord.floors || 0;
                _actualDays++;

                if ((actualRecord.totalSteps || 0) >= EXERCISE_TARGETS.STEPS) {
                    goalDays++;
                }
            }
        }

        const averageSteps = Math.round(totalSteps / 7);
        const averageDistance = Math.round((totalDistance / 7) * 100) / 100;
        const averageCalories = Math.round(totalCalories / 7);
        const averageFloors = Math.round((totalFloors / 7) * 10) / 10;

        return {
            value: averageSteps,
            distance: averageDistance,
            calories: averageCalories,
            floors: averageFloors,
            totalSteps: totalSteps,
            goalDays: goalDays,
            totalDays: 7
        };
    };

    // 현재 월간 데이터를 기반으로 통계 계산
    const calculateCurrentMonthStats = () => {
        const currentMonthData = getCurrentMonthData();
        if (currentMonthData.length === 0) {
            return {
                value: 0,
                distance: 0,
                calories: 0,
                floors: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0
            };
        }

        // 해당 월의 실제 데이터 가져오기
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        const year = targetMonth.getFullYear();
        const month = targetMonth.getMonth() + 1;
        const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;
        const totalDaysInMonth = isCurrentMonth ? Math.min(today.getDate(), new Date(year, month, 0).getDate()) : new Date(year, month, 0).getDate();

        let totalSteps = 0;
        let totalDistance = 0;
        let totalCalories = 0;
        let totalFloors = 0;
        let goalDays = 0;
        let _actualDays = 0;

        for (let day = 1; day <= totalDaysInMonth; day++) {
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            const actualRecord = physicalRecords.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            if (actualRecord) {
                totalSteps += actualRecord.totalSteps || 0;
                totalDistance += actualRecord.distance || 0;
                totalCalories += actualRecord.calories || 0;
                totalFloors += actualRecord.floors || 0;
                _actualDays++;

                if ((actualRecord.totalSteps || 0) >= EXERCISE_TARGETS.STEPS) {
                    goalDays++;
                }
            }
        }

        const averageSteps = Math.round(totalSteps / totalDaysInMonth);
        const averageDistance = Math.round((totalDistance / totalDaysInMonth) * 100) / 100;
        const averageCalories = Math.round(totalCalories / totalDaysInMonth);
        const averageFloors = Math.round((totalFloors / totalDaysInMonth) * 10) / 10;

        return {
            value: averageSteps,
            distance: averageDistance,
            calories: averageCalories,
            floors: averageFloors,
            totalSteps: totalSteps,
            goalDays: goalDays,
            totalDays: totalDaysInMonth
        };
    };

    // 현재 연간 데이터를 기반으로 통계 계산
    const calculateCurrentYearStats = () => {
        const currentYearData = getCurrentYearData();
        if (currentYearData.length === 0) {
            return {
                value: 0,
                distance: 0,
                calories: 0,
                floors: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0,
                bestDay: '',
                worstDay: ''
            };
        }

        // 해당 연도의 실제 데이터 가져오기
        const today = new Date();
        const targetYear = new Date(today);
        targetYear.setFullYear(today.getFullYear() + currentYearIndex);

        const year = targetYear.getFullYear();

        let totalSteps = 0;
        let totalDistance = 0;
        let totalCalories = 0;
        let totalFloors = 0;
        let goalDays = 0;
        let _actualDays = 0;

        // 해당 연도의 모든 월에 대해 데이터 수집
        for (let month = 1; month <= 12; month++) {
            const monthRecords = physicalRecords.filter(record => {
                const recordDate = new Date(record.date);
                return recordDate.getFullYear() === year && recordDate.getMonth() + 1 === month;
            });

            for (const record of monthRecords) {
                totalSteps += record.totalSteps || 0;
                totalDistance += record.distance || 0;
                totalCalories += record.calories || 0;
                totalFloors += record.floors || 0;
                _actualDays++;

                if ((record.totalSteps || 0) >= EXERCISE_TARGETS.STEPS) {
                    goalDays++;
                }
            }
        }

        // 해당 연도의 총 일수 계산
        const isCurrentYear = year === today.getFullYear();
        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);

        let totalDaysInYear;
        if (isCurrentYear) {
            // 올해인 경우: 1월 1일부터 오늘까지의 일수
            const yearStart = new Date(year, 0, 1);
            const daysDiff = Math.floor((today - yearStart) / (1000 * 60 * 60 * 24)) + 1;
            totalDaysInYear = daysDiff;
        } else {
            // 과거/미래 연도인 경우: 윤년 여부에 따라 365일 또는 366일
            totalDaysInYear = isLeapYear ? 366 : 365;
        }

        const averageSteps = Math.round(totalSteps / totalDaysInYear);
        const averageDistance = Math.round((totalDistance / totalDaysInYear) * 100) / 100;
        const averageCalories = Math.round(totalCalories / totalDaysInYear);
        const averageFloors = Math.round((totalFloors / totalDaysInYear) * 10) / 10;

        // 해당 연도의 모든 데이터에서 가장 많이/적게 걸은 날 찾기
        const yearRecords = physicalRecords.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate.getFullYear() === year;
        });

        if (yearRecords.length > 0) {
            // 걸음수 기준으로 정렬 (동률일 경우 날짜가 늦은 순으로)
            const sortedBySteps = yearRecords.sort((a, b) => {
                const stepsA = a.totalSteps || 0;
                const stepsB = b.totalSteps || 0;
                if (stepsA !== stepsB) {
                    return stepsB - stepsA; // 걸음수 내림차순
                }
                // 걸음수가 같으면 날짜 내림차순 (늦은 날짜가 먼저)
                return new Date(b.date) - new Date(a.date);
            });

            const bestDay = sortedBySteps[0]; // 가장 많이 걸은 날 (동률일 경우 늦은 날짜)
            const worstDay = sortedBySteps[sortedBySteps.length - 1]; // 가장 적게 걸은 날

            const bestDayFormatted = bestDay ? formatDateTimeDisplay(bestDay.date, 'monthDay') : '';
            const worstDayFormatted = worstDay ? formatDateTimeDisplay(worstDay.date, 'monthDay') : '';

            return {
                value: averageSteps,
                distance: averageDistance,
                calories: averageCalories,
                floors: averageFloors,
                totalSteps: totalSteps,
                goalDays: goalDays,
                totalDays: totalDaysInYear,
                bestDay: bestDayFormatted,
                worstDay: worstDayFormatted
            };
        }

        return {
            value: averageSteps,
            distance: averageDistance,
            calories: averageCalories,
            floors: averageFloors,
            totalSteps: totalSteps,
            goalDays: goalDays,
            totalDays: totalDaysInYear,
            bestDay: '',
            worstDay: ''
        };
    };

    // 뷰별 데이터
    const getViewData = () => {
        switch (activeView) {
            case 1: return getCurrentMonthData();
            case 2: return getCurrentYearData();
            default: return getCurrentWeekData(); // 0 : 주간
        }
    };

    // 주간 제목 생성 (선택 언어별 날짜 표기)
    const getWeeklyTitle = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        const startFormatted = formatDateTimeDisplay(startOfWeek, 'monthDay');
        const endFormatted = formatDateTimeDisplay(endOfWeek, 'monthDay');
        return `${startFormatted} ~ ${endFormatted}`;
    };

    // 월간 제목 생성 (선택 언어별 연·월 표기)
    const getMonthlyTitle = () => {
        const currentMonthData = getCurrentMonthData();
        if (currentMonthData.length === 0) return t('no_monthly_data');

        const firstDay = currentMonthData[0].day;
        const firstDate = new Date(firstDay);

        return formatDateTimeDisplay(firstDate, 'yearMonth');
    };

    // 현재 월의 일수 계산
    const getCurrentMonthDays = () => {
        const currentMonthData = getCurrentMonthData();
        if (currentMonthData.length === 0) return 31;

        const firstDay = currentMonthData[0].day;
        const firstDate = new Date(firstDay);
        const year = firstDate.getFullYear();
        const month = firstDate.getMonth() + 1;

        // 해당 월의 마지막 날짜 계산
        const lastDay = new Date(year, month, 0).getDate();
        return lastDay;
    };

    // 연간 제목 생성 (선택 언어별 연도 표기)
    const getYearlyTitle = () => {
        const currentYearData = getCurrentYearData();
        if (currentYearData.length === 0) return t('no_yearly_data');

        const firstDay = currentYearData[0].day;
        const firstDate = new Date(firstDay);

        return formatDateTimeDisplay(firstDate, 'year');
    };

    // 뷰별 제목 (0: 주간, 1: 월간, 2: 연간)
    const getViewTitle = () => {
        const titleFns = [getWeeklyTitle, getMonthlyTitle, getYearlyTitle];
        return (titleFns[activeView] ?? getWeeklyTitle)();
    };

    // 뷰별 통계 데이터
    const getViewStats = () => {
        switch (activeView) {
            case 0: // 주간 - 현재 주간 데이터 기반으로 동적 계산
                return calculateCurrentWeekStats();
            case 1: // 월간 - 현재 월간 데이터 기반으로 동적 계산
                return calculateCurrentMonthStats();
            case 2: // 연간 - 현재 연간 데이터 기반으로 동적 계산
                return calculateCurrentYearStats();
            default:
                return calculateCurrentWeekStats();
        }
    };

    // 주간 이동 함수들
    const handlePreviousWeek = () => {
        // 이전 주로 이동 (제한 없음)
        setCurrentWeekIndex(currentWeekIndex - 1);
        // 날짜 변경 후 선택된 바 해제
        clearSelectedBar();
    };

    const handleNextWeek = () => {
        // 오늘 이후로는 이동하지 않도록 제한
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset);

        // 다음 주의 시작일이 오늘보다 미래인지 확인
        const nextWeekStart = new Date(startOfWeek);
        nextWeekStart.setDate(startOfWeek.getDate() + (currentWeekIndex + 1) * 7);

        if (nextWeekStart <= today) {
            setCurrentWeekIndex(currentWeekIndex + 1);
            // 날짜 변경 후 선택된 바 해제
            clearSelectedBar();
        }
    };

    // 월간 이동 함수들
    const handlePreviousMonth = () => {
        // 이전 월로 이동 (제한 없음)
        setCurrentMonthIndex(currentMonthIndex - 1);
        // 날짜 변경 후 선택된 바 해제
        clearSelectedBar();
    };

    const handleNextMonth = () => {
        // 오늘 이후로는 이동하지 않도록 제한
        const today = new Date();
        const nextMonthIndex = currentMonthIndex + 1;

        // 다음 달의 첫째 날이 오늘보다 미래인지 확인
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + nextMonthIndex);
        const nextMonthFirstDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);

        if (nextMonthFirstDay <= today) {
            setCurrentMonthIndex(nextMonthIndex);
            // 날짜 변경 후 선택된 바 해제
            clearSelectedBar();
        }
    };

    // 연간 이동 함수들
    const handlePreviousYear = () => {
        // 이전 연도로 이동 (제한 없음)
        setCurrentYearIndex(currentYearIndex - 1);
        // 날짜 변경 후 선택된 바 해제
        clearSelectedBar();
    };

    const handleNextYear = () => {
        // 오늘 이후로는 이동하지 않도록 제한
        const today = new Date();
        const nextYearIndex = currentYearIndex + 1;

        // 다음 연도의 첫째 날이 오늘보다 미래인지 확인
        const targetYear = new Date(today);
        targetYear.setFullYear(today.getFullYear() + nextYearIndex);
        const nextYearFirstDay = new Date(targetYear.getFullYear(), 0, 1);

        if (nextYearFirstDay <= today) {
            setCurrentYearIndex(nextYearIndex);
            // 날짜 변경 후 선택된 바 해제
            clearSelectedBar();
        }
    };

    const stats = getViewStats();
    const yAxisData = calculateYAxisValues();

    return (
        <ContentArea sx={{ paddingBottom: '100px' }}>
            {/* 토글 버튼 */}
            <Toggle
                options={[t('weekly'), t('monthly'), t('yearly')]}
                value={activeView}
                onChange={(newView) => {
                    setActiveView(newView);
                    // 탭 변경 후 선택된 바 해제
                    clearSelectedBar();
                }}
                sx={{
                    flexShrink: 0,
                    height: '40px',
                    minHeight: '40px',
                }}
            />

            {/* 로딩 상태 */}
            {loading && (
                <CommonCard>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        {t('loading_data')}
                    </div>
                </CommonCard>
            )}

            {/* 에러 상태 */}
            {error && (
                <CommonCard>
                    <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                        {t('an_error_occurred_while_loading_data_message', { message: error })}
                    </div>
                </CommonCard>
            )}

            {/* 메인 콘텐츠 */}
            {!loading && !error && (
                <CommonCard>
                    {/* 메인 카드 */}
                    <ReportCard
                        activeView={activeView === 1 ? 'month' : activeView === 2 ? 'year' : 'week'}
                        title={getViewTitle()}
                        mainValue={stats.value}
                        mainUnit={t('didim_day')}
                        stats={{
                            distance: stats.distance,
                            calories: stats.calories,
                            floors: stats.floors,
                            totalSteps: stats.totalSteps,
                            goalDays: stats.goalDays,
                            totalDays: stats.totalDays,
                            bestDay: stats.bestDay,
                            worstDay: stats.worstDay
                        }}
                        chartData={getViewData()}
                        yAxisData={yAxisData}
                        monthDays={activeView === 1 ? getCurrentMonthDays() : null}
                        selectedBarIndex={selectedBarIndex}
                        onBarSelect={setSelectedBarIndex}
                        onPreviousWeek={handlePreviousWeek}
                        onNextWeek={handleNextWeek}
                        onPreviousMonth={handlePreviousMonth}
                        onNextMonth={handleNextMonth}
                        onPreviousYear={handlePreviousYear}
                        onNextYear={handleNextYear}
                    />
                </CommonCard>
            )}
        </ContentArea>
    );
};

export default ReportStepUnion;